/** User-visible publish error text from API post payloads. */
export function getPostPublishError(post) {
  if (!post) return '';
  const detail = String(post.error_detail || '').trim();
  const message = String(post.error_message || '').trim();
  const auditReason = String(post.audit?.failure_reason || '').trim();
  return detail || message || auditReason || '';
}

function formatValidationItem(item) {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return String(item);
  const msg =
    (typeof item.msg === 'string' && item.msg.trim()) ||
    (typeof item.message === 'string' && item.message.trim()) ||
    '';
  if (msg) {
    const loc = Array.isArray(item.loc)
      ? item.loc.filter((p) => p !== 'body' && p !== 'query').join('.')
      : '';
    return loc ? `${loc}: ${msg}` : msg;
  }
  try {
    return JSON.stringify(item);
  } catch {
    return 'Validation error';
  }
}

/** Extract FastAPI/axios error detail for toasts. */
export function formatApiError(err, fallback = 'Something went wrong') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) {
    return detail.map(formatValidationItem).filter(Boolean).join('; ');
  }
  if (detail && typeof detail === 'object') {
    return formatValidationItem(detail);
  }
  const msg = err?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  return fallback;
}

/**
 * Coerce anything passed to showError/showSuccess into a safe React child string.
 * Prevents React #31 when callers pass FastAPI validation objects ({type, loc, msg, input}).
 */
export function coerceToastMessage(message, fallback = 'Something went wrong') {
  if (message == null || message === false) return '';
  if (typeof message === 'string') return message;
  if (typeof message === 'number' || typeof message === 'boolean') {
    return String(message);
  }
  if (message?.response?.data != null || message instanceof Error) {
    return formatApiError(message, fallback);
  }
  if (Array.isArray(message)) {
    return formatApiError({ response: { data: { detail: message } } }, fallback);
  }
  if (typeof message === 'object') {
    if (message.detail != null) {
      return formatApiError({ response: { data: { detail: message.detail } } }, fallback);
    }
    return formatValidationItem(message) || fallback;
  }
  return String(message);
}

/** Post is waiting on Celery / platform publish. */
export function isPostPublishInFlight(post) {
  if (!post) return false;
  const ps = String(post.publish_status || '').toLowerCase();
  return ps === 'queued' || ps === 'publishing';
}

export function buildPublishFailureToast(post) {
  const title = post?.title?.trim() || `Post #${post?.id ?? '?'}`;
  const platform = post?.platform ? ` (${post.platform})` : '';
  const err = getPostPublishError(post) || 'Unknown error';
  return `${title}${platform} failed to publish: ${err}`;
}
